import React from 'react';
import { Card } from '@frontend/shadcn/components/ui/card';
import { formatInTimeZone } from 'date-fns-tz';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBolt, faBuildingColumns, faCircleInfo, faFile, faListCheck } from '@fortawesome/free-solid-svg-icons';
import { ProgramTimePeriod } from '@frontend/app/types/globalTypes';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@frontend/shadcn/components/ui/hover-card';
import { P } from '@frontend/shadcn/components/typography/p';
import { H4 } from '@frontend/shadcn/components/typography/h4';
import { H3 } from '@frontend/shadcn/components/typography/h3';
import { ClientFeature } from '@frontend/app/constants';
import { useClientFeatures } from '@frontend/context/ClientFeatureContext';
import { getSpecIconAndBgColor } from '../utils';
import { iconOptions } from './ImageIconSelector';
import { useCampaign } from '../CampaignContext';

interface TimePeriodDisplayProps {
  value: number;
  timePeriod: ProgramTimePeriod;
  prefix?: string;
}

const TimePeriodDisplay: React.FC<TimePeriodDisplayProps> = ({ value, timePeriod, prefix = '$' }) => {
  const getTimePeriodText = (period: ProgramTimePeriod) => {
    switch (period) {
      case ProgramTimePeriod.PER_MONTH:
        return 'per month';
      case ProgramTimePeriod.PER_YEAR:
        return 'per year';
      default:
        return 'total';
    }
  };

  return (
    <span className="text-sm">
      {prefix}
      {value} {getTimePeriodText(timePeriod)}
    </span>
  );
};

export const PreviewCampaignSnapshot: React.FC = () => {
  const {
    campaignName,
    templateName,
    startDate,
    endDate,
    budget,
    budgetTimePeriod,
    salesGoal,
    salesGoalTimePeriod,
    creatorCount,
    creatorsTimePeriod,
    campaignImage,
    campaignIcon,
    worklets,
  } = useCampaign();
  const { [ClientFeature.BUDGET_ALLOCATION]: isBudgetAllocationEnabled } = useClientFeatures();

  // eslint-disable-next-line no-confusing-arrow
  const getIconOptionByName = (name: string | null) =>
    name ? iconOptions.find((opt) => opt.name === name) : undefined;

  const selectedIcon = getIconOptionByName(campaignIcon || null);

  return (
    <div className="p-6 h-full">
      <div className="sticky top-0 flex flex-col gap-4">
        <div className="flex items-center gap-2">
          <div className="bg-grey-6 rounded-lg p-2 h-6 w-6 flex items-center justify-center">
            <FontAwesomeIcon icon={faBolt} className="text-white" />
          </div>
          <H3 className="font-medium p-0 m-0">Campaign Snapshot</H3>
        </div>

        <Card className="shadow-md overflow-hidden">
          <div className={`w-full h-[220px] relative p-6 ${!campaignImage ? 'bg-blue-400' : ''}`}>
            {campaignImage ? (
              <div className="absolute inset-0 w-full h-full p-4">
                <img src={campaignImage} alt="Campaign" className="w-full h-full object-cover rounded-lg" />
              </div>
            ) : selectedIcon ? (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="p-8 rounded-full">
                  <FontAwesomeIcon icon={selectedIcon.icon} className="h-16 w-16 text-white" />
                </div>
              </div>
            ) : (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="p-8 rounded-full">
                  <div className="text-white text-5xl font-light">{templateName?.charAt(0) || 'P'}</div>
                </div>
              </div>
            )}
          </div>

          <div className="p-4">
            <div className="mb-4">
              <H4 className="font-semibold text-gray-800">{campaignName || 'Enter campaign name'}</H4>
              <div className="flex items-center gap-2 text-gray-500 mt-1">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                <span className="text-sm">
                  {startDate ? formatInTimeZone(startDate, 'UTC', 'MMM d, yyyy') : 'Mar 1, 2025'}
                  {endDate ? ` - ${formatInTimeZone(endDate, 'UTC', 'MMM d, yyyy')}` : ' - Ongoing'}
                </span>
              </div>
            </div>

            <div className="mb-4">
              <div className="flex items-center gap-2 mb-2">
                <FontAwesomeIcon icon={faListCheck} />
                <P className="text-sm font-medium text-[#162428]">Workflow Stages</P>
                <HoverCard>
                  <HoverCardTrigger asChild>
                    <FontAwesomeIcon icon={faCircleInfo} className="text-grey-3 cursor-pointer" />
                  </HoverCardTrigger>
                  <HoverCardContent className="w-80">
                    <div className="flex flex-col gap-1">
                      <h4 className="font-medium">Workflow Stages</h4>
                      <p className="text-sm text-muted-foreground">
                        Launch your campaign with default workflow stages. Workflow can be configured and customized
                        after the campaign is created.
                      </p>
                    </div>
                  </HoverCardContent>
                </HoverCard>
              </div>
              <div className="flex gap-2">
                {worklets?.map((worklet, index) => {
                  const { icon, bgColor } = getSpecIconAndBgColor(worklet?.specURI, 12);
                  return (
                    <div
                      key={worklet.specKey || index}
                      className={`w-8 h-8 rounded text-white flex items-center justify-center ${bgColor}`}
                    >
                      {icon}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="mb-4">
              <div className="flex items-center gap-1 mb-2">
                <FontAwesomeIcon icon={faFile} className="text-grey-3" />
                <span className="text-sm font-medium text-gray-600">About</span>
                <FontAwesomeIcon icon={faCircleInfo} className="text-grey-3 cursor-pointer" />
              </div>
              <ul className="list-disc pl-6 space-y-1 text-gray-600">
                {budget && (
                  <li>
                    <div className="flex items-center justify-between">
                      <span className="text-sm">
                        <span className="font-medium">Budget:</span>{' '}
                        <TimePeriodDisplay value={budget} timePeriod={budgetTimePeriod} />
                      </span>
                      {isBudgetAllocationEnabled && (
                        <span className="flex items-center gap-1 text-sm text-white bg-green-4 px-2 py-1 rounded-lg">
                          <FontAwesomeIcon icon={faBuildingColumns} />
                          Master Budget
                        </span>
                      )}
                    </div>
                  </li>
                )}
                {salesGoal && (
                  <li>
                    <span className="text-sm">
                      <span className="font-medium">Sales Goal:</span>{' '}
                      <TimePeriodDisplay value={salesGoal} timePeriod={salesGoalTimePeriod} />
                    </span>
                  </li>
                )}
                {creatorCount && (
                  <li>
                    <span className="text-sm">
                      <span className="font-medium">Active Collaborators Goal:</span>{' '}
                      <TimePeriodDisplay value={creatorCount} timePeriod={creatorsTimePeriod} prefix="" />
                    </span>
                  </li>
                )}
              </ul>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};
